import { LitElement, html, css} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import '../../voyage-popup/src/voyage-popup';
import '../../voy-timeinput/src/voy-timeinput';

import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { GetFormattedDate, ConvertDateToAnotherTimeZone } from '../../../src/utils/helpers';
import { TerminalsModel } from '../../../src/models/terminals-model';
import { SimplifiedPortCall } from '../../../src/models/types/voyage-schedules-model.types';

@customElement("vessel-edit-schedule-warning")
export class VesselEditScheduleWarning extends LitElement {

    @property({attribute: false}) userModifiedPort: SimplifiedPortCall | null = null;
    @property({attribute: false}) conflictPort: SimplifiedPortCall | null = null;
    @property({type: Boolean}) shown: boolean = false;

    constructor() {
        super();
        this._populateData = this._populateData.bind(this);
    }

    static get styles() {
        return [
            css`
                .container {
                    display: grid;
                    grid-template-rows: auto auto auto;
                    place-content: center;
                    grid-gap: 20px;
                    min-width: 713px;
                    padding: 20px 0 20px 0;
                    border: 1px solid var(--neutrals-grey-07);
                    background: var(--neutrals-grey-10);
                    font-family: 'MaerskText';
                    font-size: 14px;
                }

                .warning-icon {
                    color: var(--voyage-error-red);
                    place-content: center;
                    display: grid;
                }

                .warning-icon > svg {
                    width: 41px; 
                    height: 37px;
                }

                .warning-text {
                    text-align: center;
                    color: var(--neutrals-grey-02);
                }

                .warning-schedules {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    place-content: center;
                    background: var(--schedule-edit-warning-background);
                    border: 1px solid var(--schedule-edit-warning-background-border);
                }

                .port-call {
                    display: grid;
                    place-content: center;
                    grid-template-rows: auto auto auto;
                    padding-top: 20px;
                    padding-bottom: 20px;
                    grid-gap: 10px;
                }

                .header {
                    color: var(--neutrals-grey-02);
                    font-weight: bold;
                }

                .group {
                    display: grid;
                    grid-template-rows: auto auto;
                }

                .label-header {
                    font-size: 10px;
                    color: var(--schedule-edit-warning-header);
                }

                .text {
                    font-size: 18px;
                    color: var(--neutrals-grey-02);
                }
        `];
    }

    render() {
        return html`
            <voyage-popup no-close-button ?shown=${this.shown} header-text=${`Conflict with existing schedule`} .rejectButtonText=${""} @close=${this._onClose} @accept=${this._onAccept} accept-button-text=${"Ok"} no-overflow>
                <div class="container">
                    <div class="warning-icon">${SvgIcons.exclamationTriangle}</div>
                    <div class="warning-text">The change you made has a conflict with the schedule.<br>Please review your changes.</div>
                    <div class="warning-schedules">
                        <div class="port-call user">
                            <div class="header">YOUR CHANGE</div>
                            <div class="group">
                                <div class="label-header">Port</div>
                                <div class="text user port">${this._userModifiedPortName}</div>
                            </div>
                            <div class="group">
                                <div class="label-header">ETA (local)</div>
                                <div class="text user eta">${this._userModifiedPortETA}</div>
                            </div>
                            <div class="group">
                                <div class="label-header">ETD (local)</div>
                                <div class="text user etd">${this._userModifiedPortETD}</div>
                            </div>
                        </div>
                        <div class="port-call conflict">
                            <div class="header">CONFLICT</div>
                            <div class="group">
                                <div class="label-header">Port</div>
                                <div class="text conflict port">${this._conflictPortName}</div>
                            </div>
                            <div class="group">
                                <div class="label-header">ETA (local)</div>
                                <div class="text conflict eta">${this._conflictPortETA}</div>
                            </div>
                            <div class="group">
                                <div class="label-header">ETD (local)</div>
                                <div class="text conflict etd">${this._conflictPortETD}</div>
                            </div>
                        </div>
                    
                    </div>
                </div>
            </voyage-popup>
       `;
    }

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has("userModifiedPort") || changedProperties.has("conflictPort")) {
            this._populateData();
        }

        super.updated(changedProperties);
    }

    private async _populateData() {
        this._userModifiedPortName = TerminalsModel!.getTerminal(this.userModifiedPort!.terminalCode)!.city;
        this._conflictPortName = TerminalsModel!.getTerminal(this.conflictPort!.terminalCode)!.city;
        this._userModifiedPortETA = GetFormattedDate(ConvertDateToAnotherTimeZone(this.userModifiedPort!.arrivalAt, TerminalsModel!.getTerminal(this.userModifiedPort!.terminalCode)!.olsonTimezone!));
        this._conflictPortETA = GetFormattedDate(ConvertDateToAnotherTimeZone(this.conflictPort!.arrivalAt, TerminalsModel!.getTerminal(this.conflictPort!.terminalCode)!.olsonTimezone!));
        this._userModifiedPortETD = GetFormattedDate(ConvertDateToAnotherTimeZone(this.userModifiedPort!.departureAt, TerminalsModel!.getTerminal(this.userModifiedPort!.terminalCode)!.olsonTimezone!));
        this._conflictPortETD = GetFormattedDate(ConvertDateToAnotherTimeZone(this.conflictPort!.departureAt, TerminalsModel!.getTerminal(this.conflictPort!.terminalCode)!.olsonTimezone!));

        this.requestUpdate();
    }

    private _userModifiedPortName: string = "";
    private _conflictPortName: string = "";
    private _userModifiedPortETA: string = "";
    private _conflictPortETA: string = "";
    private _userModifiedPortETD: string = "";
    private _conflictPortETD: string = "";

    private _onClose(){
        this.dispatchEvent(new Event('cancel'));
    }

    private async _onAccept(){
        this.dispatchEvent(new Event('cancel'));
    }    
}
